import { createSlice } from '@reduxjs/toolkit';
import { t } from 'i18next';
import { showSuccess } from '../../app/utils/sweetAlert';
import { showSuccessToast } from '../../app/utils/tostify';
import { deleteProfilePicture, updateProfilePicture } from '../apis/commonAPIs';
import {
	addSystemUser,
	deleteSystemUser,
	editSystemUser,
	getAssignTenantsList,
	getSystemUserDetails,
	getUsersList,
	updateSystemUsersStatus
} from '../apis/systemUserAPI';
import {
	getSettingUserList,
	getUserDetails,
	updateUsersStatus
} from '../apis/usersAPIs';

interface SystemUserState {
	loading: boolean;
	updatingProfileImage: boolean;
	selectedSystemUsers: Array<any>;
	systemUserList: Array<any>;
	assignTenantList: Array<any>;
	systemUserCount: number;
	assignTenantCount: number;
	currentPageInUserList: number | string;
	currentPageInAssignTenant: number | string;
	userDetails: Object;
	profileImageUrl: any;
	profileImageFile: File | null;
	userRoleInfo: Object;
	roleType: any;
	searchKey: string;
	filter: any;
}

const initialState: SystemUserState = {
	loading: false,
	updatingProfileImage: false,
	selectedSystemUsers: [],
	systemUserList: [],
	assignTenantList: [],
	systemUserCount: 0,
	assignTenantCount: 0,
	currentPageInAssignTenant: 1,
	currentPageInUserList: 1,
	userDetails: {},
	profileImageUrl: null,
	profileImageFile: null,
	userRoleInfo: {},
	roleType: '',
	searchKey: '',
	filter: null
};

const systemuserSlice = createSlice({
	name: 'common',
	initialState,
	reducers: {
		handlePaginationInSystemUser(state, action: any) {
			state.currentPageInUserList = action.payload;
		},
		handlePaginationInAssignTenants(state, action: any) {
			state.currentPageInAssignTenant = action.payload;
		},
		updateSelectedSystemUsers(state, action: any) {
			const indexOf = state.selectedSystemUsers?.findIndex(
				(item: any) => item.id == action.payload.id
			);
			if (indexOf == -1) {
				state.selectedSystemUsers.push(action.payload);
			} else {
				state.selectedSystemUsers.splice(indexOf, 1);
			}
		},
		addListArrayInStatusList(state, action: any) {
			state.selectedSystemUsers = action.payload;
		},
		setProfileImage(state, action: any) {
			state.profileImageUrl = action.payload.url;
			state.profileImageFile = action.payload.file;
		},
		handleRoleId(state, action) {
			state.roleType = action.payload;
		},
		handleSearchKey(state, action: any) {
			state.searchKey = action.payload;
		},
		handleFilter(state, action: any) {
			state.filter = action.payload;
		}
	},
	extraReducers: (builder) => {
		builder.addCase(getUsersList.pending, (state) => {
			state.loading = true;
			state.userDetails = {};
			state.userRoleInfo = {};
			state.profileImageUrl = null;
		});
		builder.addCase(getUsersList.fulfilled, (state, action: any) => {
			state.systemUserList = action.payload.list;
			state.systemUserCount = action.payload.count;
			state.profileImageUrl = null;
			state.loading = false;
		});
		builder.addCase(getUsersList.rejected, (state) => {
			state.loading = false;
		});
		builder.addCase(getSystemUserDetails.pending, (state) => {
			state.loading = true;
			state.profileImageUrl = null;
			state.profileImageFile = null;
		});
		builder.addCase(
			getSystemUserDetails.fulfilled,
			(state, action: any) => {
				const { userDetails, userRoleInfo } = action.payload;
				const profilePic = userRoleInfo.profile_pic;
				state.userDetails = userDetails;
				state.userRoleInfo = userRoleInfo;
				state.profileImageUrl = profilePic ? profilePic : null;
				state.loading = false;
			}
		);
		builder.addCase(getSystemUserDetails.rejected, (state) => {
			state.loading = false;
		});
		builder.addCase(getUserDetails.pending, (state) => {
			state.loading = true;
			state.profileImageUrl = null;
			state.profileImageFile = null;
		});
		//for user section or user slice
		builder.addCase(getUserDetails.fulfilled, (state, action: any) => {
			const { profile_pic } = (action.payload.data);
			const profilePic = profile_pic;
			state.profileImageUrl = profilePic ? profilePic : null;
			state.loading = false;
		});
		builder.addCase(getUserDetails.rejected, (state) => {
			state.loading = false;
		});
		builder.addCase(addSystemUser.pending, (state: SystemUserState) => {
			state.loading = true;
		});
		builder.addCase(
			addSystemUser.fulfilled,
			(state: SystemUserState, action: any) => {
				state.loading = false;
				showSuccessToast({ message: t(action.payload.message) });
			}
		);
		builder.addCase(addSystemUser.rejected, (state: SystemUserState) => {
			state.loading = false;
		});
		builder.addCase(editSystemUser.pending, (state: SystemUserState) => {
			state.loading = true;
		});
		builder.addCase(
			editSystemUser.fulfilled,
			(state: SystemUserState, action: any) => {
				state.loading = false;
				showSuccess({ message: t(action.payload.message) });
			}
		);
		builder.addCase(editSystemUser.rejected, (state: SystemUserState) => {
			state.loading = false;
		});
		builder.addCase(deleteSystemUser.pending, (state: SystemUserState) => {
			state.loading = true;
		});
		builder.addCase(
			deleteSystemUser.fulfilled,
			(state: SystemUserState, action: any) => {
				state.loading = false;
				showSuccess({ message: t(action.payload.message) });
			}
		);
		builder.addCase(deleteSystemUser.rejected, (state: SystemUserState) => {
			state.loading = false;
		});
		builder.addCase(
			updateProfilePicture.pending,
			(state: SystemUserState) => {
				state.updatingProfileImage = true;
			}
		);
		builder.addCase(
			updateProfilePicture.rejected,
			(state: SystemUserState) => {
				state.updatingProfileImage = false;
			}
		);
		builder.addCase(
			updateProfilePicture.fulfilled,
			(state: SystemUserState) => {
				state.updatingProfileImage = false;
			}
		);
		builder.addCase(
			deleteProfilePicture.pending,
			(state: SystemUserState) => {
				state.updatingProfileImage = true;
			}
		);
		builder.addCase(
			deleteProfilePicture.rejected,
			(state: SystemUserState) => {
				state.updatingProfileImage = false;
			}
		);
		builder.addCase(
			deleteProfilePicture.fulfilled,
			(state: SystemUserState) => {
				state.updatingProfileImage = false;
			}
		);
		builder.addCase(getAssignTenantsList.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(
			getAssignTenantsList.fulfilled,
			(state, action: any) => {
				state.assignTenantList = action.payload.list;
				state.assignTenantCount = action.payload.count;
				state.loading = false;
			}
		);
		builder.addCase(getAssignTenantsList.rejected, (state) => {
			state.loading = false;
		});
		builder.addCase(
			updateSystemUsersStatus.fulfilled,
			(state: SystemUserState) => {
				state.selectedSystemUsers = [];
			}
		);
		builder.addCase(
			updateUsersStatus.fulfilled,
			(state: SystemUserState, action: any) => {
				state.selectedSystemUsers = [];
				showSuccess({ message: t(action.payload.message) });
			}
		);
		//during setting user listing api call we need to clear the preview image so thats why||
		builder.addCase(getSettingUserList.pending, (state) => {
			state.profileImageUrl = null;
			state.profileImageFile = null;
		});
	}
});

export const {
	handlePaginationInSystemUser,
	handlePaginationInAssignTenants,
	updateSelectedSystemUsers,
	addListArrayInStatusList,
	setProfileImage,
	handleRoleId,
	handleSearchKey,
	handleFilter
} = systemuserSlice.actions;
export default systemuserSlice.reducer;
